import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress
} from '@mui/material';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';

/* ONE card-entry form, shared by the Payments tab and the billing-migration
 * prompt on the dashboard.
 *
 * It was private to SettingsUserPayments. The prompt needs exactly the same
 * thing — collect a card, then either set it as default or move the
 * subscription — and copying it would have meant two implementations of a flow
 * that takes money, drifting apart the first time one of them was fixed. The
 * modal is a different frame around the same form, not a second one.
 */

/* Card-entry form, rendered inside <Elements> so it can use the Stripe hooks. The
 * SetupIntent is created first, server-side — on the account that bills the member's
 * subscription, or on the new account for anyone without one — so the provider
 * already holds the client secret and confirmSetup needs no elements.submit(). On
 * success the new payment method goes to the server, which sets it as default on BOTH
 * the customer and the active subscription.
 *
 * `renewsOnOldSystem` (update mode only): the subscription this card is for still
 * bills on the OLD Stripe account and cannot be moved yet, so the card is saved there
 * and only its renewals use it. Promising "future payments" would be untrue —
 * purchases run on the new account, and this card will not be offered at checkout. */
export const UpdateCardForm = ({
  mode,
  renewsOnOldSystem = false,
  onDone,
  onCancel
}: {
  mode: 'update' | 'migrate';
  renewsOnOldSystem?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [saving, setSaving] = useState(false);
  const isMigrate = mode === 'migrate';

  const handleSave = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required'
    });
    if (error) {
      showSnackbar(error.message || 'Card setup failed.', 'error');
      setSaving(false);
      return;
    }
    const paymentMethodId = setupIntent?.payment_method as string;
    if (!paymentMethodId) {
      showSnackbar('Card setup did not complete. Please try again.', 'error');
      setSaving(false);
      return;
    }
    try {
      if (isMigrate) {
        // The card was collected on the NEW account; move the subscription over.
        const res = await post(
          'user/subscription/migrate',
          { paymentMethodId },
          true
        );
        const when = res?.firstChargeAt
          ? new Date(res.firstChargeAt * 1000).toLocaleDateString()
          : null;
        showSnackbar(
          res?.alreadyMigrated
            ? 'Your billing is already on the new system.'
            : when
            ? `You're moved to the new billing system — no charge today. Your next payment is on ${when} (your existing renewal date).`
            : "You're moved to the new billing system.",
          'success'
        );
        onDone();
        return;
      }
      const res = await post(
        'user/payment-method/default',
        { paymentMethodId },
        true
      );
      if (!res?.success) {
        showSnackbar(res?.message || 'Could not save the new card.', 'error');
        setSaving(false);
        return;
      }
      /* The server says what actually happened. For a member whose payment had
       * failed it also retries it, and "Payment method updated." alone would hide
       * whether that went through. */
      showSnackbar(
        res?.message || 'Payment method updated.',
        res?.invoiceRetry === 'failed' ? 'warning' : 'success'
      );
      onDone();
    } catch (e: any) {
      const msg =
        e?.data?.error?.message ||
        e?.response?.data?.error?.message ||
        e?.data?.message ||
        e?.response?.data?.message ||
        (isMigrate
          ? 'Could not move your billing. Please try again.'
          : 'Could not save the new card. Please try again.');
      showSnackbar(msg, 'error');
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mt: 2.5 }}>
      <CardContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box sx={{ minHeight: 220 }}>
          <PaymentElement options={{ paymentMethodOrder: ['card'] }} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {isMigrate
            ? 'We move your subscription to our new billing system with this card. You are not charged today — your next payment is on your existing renewal date, and your giveaway entries carry over.'
            : renewsOnOldSystem
            ? 'This card replaces the one your subscription is billed to, and is used for its renewals.'
            : 'Your new card replaces the one on file and is used for future payments, including your subscription renewal.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!stripe || saving}
          >
            {saving ? (
              <CircularProgress size={20} />
            ) : isMigrate ? (
              'Move to new billing'
            ) : (
              'Save card'
            )}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
